import React, { useState } from 'react';
import { Plus, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { Input } from '../ui/Input';
import { Checkbox } from '../ui/Checkbox';
import { useFormScoring } from '../../hooks/useFormScoring';
import type { ScoringConfig, ScoreVariable, ScoreRange, FormField } from '../../types/forms';
import { normalizeFieldOption } from '../../types/forms';

const SCORABLE_TYPES = [
  'radio', 'select', 'checkbox', 'multiselect',
  'picture_choice', 'yes_no', 'opinion_scale', 'nps', 'rating',
];

interface ScoringSettingsProps {
  scoringConfig: ScoringConfig;
  onUpdate: (config: ScoringConfig) => void;
  fields: FormField[];
}

// =====================================================
// SCORE PREVIEW SUB-COMPONENT
// =====================================================

const ScorePreview: React.FC<{ fields: FormField[]; scoringConfig: ScoringConfig }> = ({
  fields,
  scoringConfig,
}) => {
  const [simulatedData, setSimulatedData] = useState<Record<string, any>>({});

  const { scores } = useFormScoring({
    fields,
    formData: simulatedData,
    scoringConfig,
  });

  const scorableFields = fields.filter(f =>
    SCORABLE_TYPES.includes(f.type) && f.options?.some(opt =>
      typeof opt !== 'string' && opt.scores?.length
    )
  );

  if (!scorableFields.length) {
    return (
      <p className="text-xs text-neutral-500">
        Nenhum campo com pontuação configurada. Atribua pontos às opções dos campos no editor lateral.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {scorableFields.map(field => (
        <div key={field.id} className="bg-neutral-900/50 rounded-xl p-3">
          <p className="text-xs text-neutral-400 mb-1">{field.label}</p>
          <select
            value={simulatedData[field.id] || ''}
            onChange={(e) => setSimulatedData(prev => ({ ...prev, [field.id]: e.target.value }))}
            className="w-full bg-neutral-800 border border-neutral-700 rounded-lg px-3 py-1.5 text-base sm:text-sm text-white"
          >
            <option value="">Sem resposta</option>
            {field.options?.map((opt, i) => {
              const normalized = normalizeFieldOption(opt);
              return <option key={i} value={normalized.value}>{normalized.label}</option>;
            })}
          </select>
        </div>
      ))}

      {scores && (
        <div className="mt-4 space-y-2">
          {scoringConfig.variables.map(variable => {
            const result = scores[variable.id];
            if (!result) return null;
            return (
              <div key={variable.id} className="flex items-center justify-between bg-neutral-900/50 rounded-xl p-3">
                <span className="text-sm text-neutral-300">{variable.name}</span>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-white">{result.totalPoints} pts</span>
                  {result.range && (
                    <span
                      className="px-2 py-0.5 rounded-full text-xs font-medium"
                      style={{
                        backgroundColor: `${result.range.color || '#6366f1'}20`,
                        color: result.range.color || '#6366f1',
                      }}
                    >
                      {result.range.label}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// =====================================================
// SCORING SETTINGS MAIN COMPONENT
// =====================================================

export const ScoringSettings: React.FC<ScoringSettingsProps> = ({
  scoringConfig,
  onUpdate,
  fields,
}) => {
  const [expandedVar, setExpandedVar] = useState<string | null>(null);

  const handleToggleEnabled = (enabled: boolean) => {
    onUpdate({ ...scoringConfig, enabled });
  };

  const handleToggleShowResult = (showResultToRespondent: boolean) => {
    onUpdate({ ...scoringConfig, showResultToRespondent });
  };

  const handleAddVariable = () => {
    const newVar: ScoreVariable = {
      id: `var_${Date.now()}`,
      name: 'Nova variável',
      ranges: [],
    };
    onUpdate({
      ...scoringConfig,
      variables: [...scoringConfig.variables, newVar],
    });
    setExpandedVar(newVar.id);
  };

  const handleUpdateVariable = (varId: string, updates: Partial<ScoreVariable>) => {
    onUpdate({
      ...scoringConfig,
      variables: scoringConfig.variables.map(v =>
        v.id === varId ? { ...v, ...updates } : v
      ),
    });
  };

  const handleDeleteVariable = (varId: string) => {
    onUpdate({
      ...scoringConfig,
      variables: scoringConfig.variables.filter(v => v.id !== varId),
    });
    if (expandedVar === varId) setExpandedVar(null);
  };

  const handleAddRange = (varId: string) => {
    const variable = scoringConfig.variables.find(v => v.id === varId);
    if (!variable) return;
    const lastMax = variable.ranges.length > 0
      ? variable.ranges[variable.ranges.length - 1].max
      : 0;
    const newRange: ScoreRange = {
      min: lastMax + 1,
      max: lastMax + 50,
      label: 'Nova faixa',
      color: '#6366f1',
    };
    handleUpdateVariable(varId, {
      ranges: [...variable.ranges, newRange],
    });
  };

  const handleUpdateRange = (varId: string, rangeIndex: number, updates: Partial<ScoreRange>) => {
    const variable = scoringConfig.variables.find(v => v.id === varId);
    if (!variable) return;
    const newRanges = variable.ranges.map((r, i) =>
      i === rangeIndex ? { ...r, ...updates } : r
    );
    handleUpdateVariable(varId, { ranges: newRanges });
  };

  const handleDeleteRange = (varId: string, rangeIndex: number) => {
    const variable = scoringConfig.variables.find(v => v.id === varId);
    if (!variable) return;
    handleUpdateVariable(varId, {
      ranges: variable.ranges.filter((_, i) => i !== rangeIndex),
    });
  };

  return (
    <div className="space-y-6">
      {/* Enable/Disable Toggle */}
      <Checkbox
        label="Ativar pontuação"
        checked={scoringConfig.enabled}
        onChange={(e) => handleToggleEnabled(e.target.checked)}
      />

      {scoringConfig.enabled && (
        <>
          <Checkbox
            label="Mostrar resultado ao respondente"
            checked={scoringConfig.showResultToRespondent}
            onChange={(e) => handleToggleShowResult(e.target.checked)}
            helpText="Exibe a pontuação na tela de agradecimento"
          />

          {/* Variables List */}
          <div>
            <p className="text-xs text-neutral-500 uppercase mb-3">Variáveis de score</p>

            <div className="space-y-3">
              {scoringConfig.variables.map(variable => {
                const isExpanded = expandedVar === variable.id;

                return (
                  <div
                    key={variable.id}
                    className="border border-neutral-700/50 rounded-xl overflow-hidden"
                  >
                    {/* Variable Header */}
                    <button
                      onClick={() => setExpandedVar(isExpanded ? null : variable.id)}
                      className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-neutral-800/30 transition-colors"
                    >
                      <span className="text-sm font-medium text-neutral-200">{variable.name}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-neutral-500">
                          {variable.ranges.length} faixa{variable.ranges.length !== 1 ? 's' : ''}
                        </span>
                        {isExpanded ? (
                          <ChevronUp className="w-4 h-4 text-neutral-400" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-neutral-400" />
                        )}
                      </div>
                    </button>

                    {/* Variable Body (Expanded) */}
                    {isExpanded && (
                      <div className="px-4 pb-4 space-y-4 border-t border-neutral-700/50">
                        <div className="pt-3">
                          <Input
                            label="Nome da variável"
                            value={variable.name}
                            onChange={(e) => handleUpdateVariable(variable.id, { name: e.target.value })}
                          />
                        </div>

                        {/* Ranges */}
                        <div>
                          <p className="text-xs text-neutral-500 uppercase mb-2">Faixas</p>
                          <div className="space-y-2">
                            {variable.ranges.map((range, rangeIdx) => (
                              <div key={rangeIdx} className="bg-neutral-900/50 rounded-xl p-3 space-y-2">
                                <div className="flex flex-wrap sm:flex-nowrap items-center gap-2">
                                  <Input
                                    type="number"
                                    value={String(range.min)}
                                    onChange={(e) => handleUpdateRange(variable.id, rangeIdx, { min: Number(e.target.value) })}
                                    className="w-20"
                                    placeholder="Min"
                                  />
                                  <span className="hidden sm:inline text-neutral-500">-</span>
                                  <Input
                                    type="number"
                                    value={String(range.max)}
                                    onChange={(e) => handleUpdateRange(variable.id, rangeIdx, { max: Number(e.target.value) })}
                                    className="w-20"
                                    placeholder="Max"
                                  />
                                  <Input
                                    value={range.label}
                                    onChange={(e) => handleUpdateRange(variable.id, rangeIdx, { label: e.target.value })}
                                    placeholder="Label"
                                    className="flex-1"
                                  />
                                  <input
                                    type="color"
                                    value={range.color || '#6366f1'}
                                    onChange={(e) => handleUpdateRange(variable.id, rangeIdx, { color: e.target.value })}
                                    className="w-8 h-8 min-w-11 min-h-11 sm:min-w-0 sm:min-h-0 rounded-lg border border-neutral-700 cursor-pointer bg-transparent"
                                  />
                                  <button
                                    onClick={() => handleDeleteRange(variable.id, rangeIdx)}
                                    className="p-1.5 text-neutral-400 hover:text-red-400 transition-colors"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </div>

                                {/* Icon & Message (optional) */}
                                <div className="flex gap-2">
                                  <Input
                                    value={range.icon || ''}
                                    onChange={(e) => handleUpdateRange(variable.id, rangeIdx, { icon: e.target.value })}
                                    placeholder="Ícone (ex: star, trophy)"
                                    className="w-48"
                                  />
                                  <Input
                                    value={range.message || ''}
                                    onChange={(e) => handleUpdateRange(variable.id, rangeIdx, { message: e.target.value })}
                                    placeholder="Mensagem (opcional)"
                                    className="flex-1"
                                  />
                                </div>
                              </div>
                            ))}
                          </div>

                          <button
                            onClick={() => handleAddRange(variable.id)}
                            className="mt-2 w-full p-2 border border-dashed border-neutral-700 rounded-xl text-sm text-neutral-400 hover:text-white hover:border-neutral-600 transition-colors"
                          >
                            + Adicionar faixa
                          </button>
                        </div>

                        {/* Delete Variable */}
                        <button
                          onClick={() => handleDeleteVariable(variable.id)}
                          className="flex items-center gap-1.5 text-xs text-red-400 hover:text-red-300 transition-colors"
                        >
                          <Trash2 className="w-3 h-3" />
                          Excluir variável
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Add Variable */}
            <button
              onClick={handleAddVariable}
              className="mt-3 flex items-center gap-2 px-4 py-2.5 w-full border border-dashed border-neutral-700 rounded-xl text-sm text-neutral-400 hover:text-white hover:border-neutral-600 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Adicionar variável
            </button>
          </div>

          {/* Score Preview */}
          {scoringConfig.variables.length > 0 && (
            <div className="pt-6 border-t border-neutral-700/50">
              <h4 className="text-sm font-medium text-neutral-200 mb-3">Preview de pontuação</h4>
              <p className="text-xs text-neutral-500 mb-4">
                Simule respostas para ver o cálculo em tempo real
              </p>
              <ScorePreview fields={fields} scoringConfig={scoringConfig} />
            </div>
          )}
        </>
      )}
    </div>
  );
};
